import {
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  PlaylistAddCheckRounded,
  TipsAndUpdatesRounded,
  WorkspacesRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowStageTaskTemplate,
  deleteIndustrialWorkflowStageTaskTemplate,
  listIndustrialWorkflowStageTaskTemplates,
  listIndustrialWorkflowWorkAreas,
  listIndustrialWorkflowWorkReportDefinitions,
  updateIndustrialWorkflowStageTaskTemplate,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowCasePriority,
  IndustrialWorkflowStageTaskTemplate,
  IndustrialWorkflowStageTaskTemplateCreationMode,
  IndustrialWorkflowStageTaskTemplateUpsertPayload,
  IndustrialWorkflowWorkReportDefinition,
} from '../types/industrialWorkflows';

type Props = {
  processDefinitionId: string;
  stageDefinitionId: string;
};

type FormState = {
  title: string;
  description: string;

  task_type:
    | 'internal_work'
    | 'outside_system_wait';

  work_area_id: string;
  work_report_definition_id: string;

  priority: IndustrialWorkflowCasePriority;
  due_offset_days: string;

  is_blocking: boolean;
  is_active: boolean;

  display_order: number;

  creation_mode:
    IndustrialWorkflowStageTaskTemplateCreationMode;

  settings: Record<string, unknown>;
};

const EMPTY_FORM: FormState = {
  title: '',
  description: '',

  task_type: 'internal_work',

  work_area_id: '',
  work_report_definition_id: '',

  priority: 'normal',
  due_offset_days: '',

  is_blocking: true,
  is_active: true,

  display_order: 0,

  creation_mode: 'initial',

  settings: {},
};

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function parseOptionalInteger(
  value: string,
) {
  if (!value.trim()) return null;

  const parsedValue =
    Number.parseInt(
      value,
      10,
    );

  return Number.isFinite(
    parsedValue,
  )
    ? parsedValue
    : null;
}

function getPriorityLabel(
  priority: IndustrialWorkflowCasePriority,
) {
  if (priority === 'urgent') return 'Urgente';
  if (priority === 'high') return 'Alta';
  if (priority === 'low') return 'Baja';

  return 'Normal';
}

function buildPayload(
  form: FormState,
): IndustrialWorkflowStageTaskTemplateUpsertPayload {
  return {
    title:
      form.title.trim(),

    description:
      form.description.trim()
      || null,

    work_area_id:
      form.work_area_id
      || null,

    work_report_definition_id:
      form.work_report_definition_id
      || null,

    priority:
      form.priority,

    due_offset_days:
      parseOptionalInteger(
        form.due_offset_days,
      ),

    is_blocking:
      form.is_blocking,

    is_active:
      form.is_active,

    display_order:
      form.display_order,

    creation_mode:
      form.creation_mode,

    settings: {
      ...form.settings,
      task_type:
        form.task_type,
    },
  };
}

export default function IndustrialWorkflowsStageTaskTemplatesConfigSection({
  processDefinitionId,
  stageDefinitionId,
}: Props) {
  const queryClient =
    useQueryClient();

  const { enqueueSnackbar } =
    useSnackbar();

  const [
    dialogOpen,
    setDialogOpen,
  ] = useState(false);

  const [
    editingTemplate,
    setEditingTemplate,
  ] =
    useState<IndustrialWorkflowStageTaskTemplate | null>(
      null,
    );

  const [
    form,
    setForm,
  ] =
    useState<FormState>(
      EMPTY_FORM,
    );

  const [
    templatePendingDelete,
    setTemplatePendingDelete,
  ] =
    useState<IndustrialWorkflowStageTaskTemplate | null>(
      null,
    );

  const templatesQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-stage-task-templates',
        processDefinitionId,
        stageDefinitionId,
        true,
      ],
      queryFn: () =>
        listIndustrialWorkflowStageTaskTemplates(
          processDefinitionId,
          stageDefinitionId,
          true,
        ),
      retry: false,
    });

  const workAreasQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-work-areas',
        false,
      ],
      queryFn: () =>
        listIndustrialWorkflowWorkAreas(
          false,
        ),
      retry: false,
    });

  const workReportDefinitionsQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-work-report-definitions',
        processDefinitionId,
        stageDefinitionId,
        true,
      ],
      queryFn: () =>
        listIndustrialWorkflowWorkReportDefinitions(
          processDefinitionId,
          stageDefinitionId,
          true,
        ),
      retry: false,
    });

  const workAreaNameById =
    useMemo(
      () =>
        new Map(
          (
            workAreasQuery.data ?? []
          ).map(
            (area) => [
              area.id,
              area.name,
            ],
          ),
        ),
      [workAreasQuery.data],
    );

  const workReportDefinitionNameById =
    useMemo(
      () =>
        new Map(
          (
            workReportDefinitionsQuery.data ?? []
          ).map(
            (
              definition:
                IndustrialWorkflowWorkReportDefinition,
            ) => [
              definition.id,
              definition.name,
            ],
          ),
        ),
      [workReportDefinitionsQuery.data],
    );

  const initialTemplates =
    useMemo(
      () =>
        (
          templatesQuery.data ?? []
        ).filter(
          (template) =>
            template.creation_mode
            === 'initial',
        ),
      [templatesQuery.data],
    );

  const suggestedTemplates =
    useMemo(
      () =>
        (
          templatesQuery.data ?? []
        ).filter(
          (template) =>
            template.creation_mode
            === 'suggested',
        ),
      [templatesQuery.data],
    );

  const invalidateQueries =
    async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-stage-task-templates',
            processDefinitionId,
            stageDefinitionId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-stage-automation',
            processDefinitionId,
            stageDefinitionId,
          ],
        }),
      ]);
    };

  const createMutation =
    useMutation({
      mutationFn: (
        payload:
          IndustrialWorkflowStageTaskTemplateUpsertPayload,
      ) =>
        createIndustrialWorkflowStageTaskTemplate(
          processDefinitionId,
          stageDefinitionId,
          payload,
        ),
      onSuccess: async () => {
        await invalidateQueries();

        setDialogOpen(false);
        setEditingTemplate(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Tarea configurada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const updateMutation =
    useMutation({
      mutationFn: ({
        taskTemplateId,
        payload,
      }: {
        taskTemplateId: string;
        payload:
          IndustrialWorkflowStageTaskTemplateUpsertPayload;
      }) =>
        updateIndustrialWorkflowStageTaskTemplate(
          processDefinitionId,
          stageDefinitionId,
          taskTemplateId,
          payload,
        ),
      onSuccess: async () => {
        await invalidateQueries();

        setDialogOpen(false);
        setEditingTemplate(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Tarea actualizada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const deleteMutation =
    useMutation({
      mutationFn: (
        taskTemplateId: string,
      ) =>
        deleteIndustrialWorkflowStageTaskTemplate(
          processDefinitionId,
          stageDefinitionId,
          taskTemplateId,
        ),
      onSuccess: async () => {
        await invalidateQueries();

        setTemplatePendingDelete(null);
        setDialogOpen(false);
        setEditingTemplate(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Tarea configurada eliminada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });
 
  const handleCreate = (
    creationMode:
      IndustrialWorkflowStageTaskTemplateCreationMode,
  ) => {
    setEditingTemplate(null);

    setForm({
      ...EMPTY_FORM,
      creation_mode:
        creationMode,
      is_blocking:
        creationMode
        === 'initial',
    });

    setDialogOpen(true);
  };

  const handleEdit = (
    template:
      IndustrialWorkflowStageTaskTemplate,
  ) => {
    setEditingTemplate(
      template,
    );

    setForm({
      title:
        template.title,

      description:
        template.description ?? '',

      task_type:
        template.settings
          .task_type
        === 'outside_system_wait'
          ? 'outside_system_wait'
          : 'internal_work',

      work_area_id:
        template.work_area_id ?? '',

      work_report_definition_id:
        template.work_report_definition_id ?? '',

      priority:
        template.priority,

      due_offset_days:
        template.due_offset_days
        === null
          ? ''
          : String(
              template.due_offset_days,
            ),

      is_blocking:
        template.is_blocking,

      is_active:
        template.is_active,

      display_order:
        template.display_order,

      creation_mode:
        template.creation_mode,

      settings:
        template.settings ?? {},
    });

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (
      createMutation.isPending
      || updateMutation.isPending
      || deleteMutation.isPending
    ) {
      return;
    }

    setDialogOpen(false);
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      enqueueSnackbar(
        'Indica el título de la tarea.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const payload =
      buildPayload(form);

    if (editingTemplate) {
      updateMutation.mutate({
        taskTemplateId:
          editingTemplate.id,
        payload,
      });

      return;
    }

    createMutation.mutate(
      payload,
    );
  };

  const handleConfirmDelete = () => {
    if (!templatePendingDelete) {
      return;
    }

    deleteMutation.mutate(
      templatePendingDelete.id,
    );
  };

  const mutationIsPending =
    createMutation.isPending
    || updateMutation.isPending
    || deleteMutation.isPending;

  const renderTemplate = (
    template:
      IndustrialWorkflowStageTaskTemplate,
  ) => (
    <Paper
      key={template.id}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(
          template.is_active
            ? '#38bdf8'
            : '#64748b',
          0.18,
        )}`,
        bgcolor:
          'rgba(2,6,23,0.22)',
        px: 1.35,
        py: 1.15,
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent="space-between"
        spacing={1}
      >
        <Stack
          direction="row"
          spacing={0.9}
          alignItems="flex-start"
        >
          <PlaylistAddCheckRounded
            sx={{
              mt: 0.1,
              color:
                template.is_active
                  ? '#7dd3fc'
                  : '#64748b',
            }}
          />

          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {template.title}
            </Typography>

            {template.description ? (
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  mt: 0.25,
                }}
              >
                {template.description}
              </Typography>
            ) : null}

            <Stack
              direction="row"
              spacing={0.55}
              flexWrap="wrap"
              useFlexGap
              sx={{
                mt: 0.7,
              }}
            >
              {template.settings
                .task_type
              === 'outside_system_wait' ? (
                <Chip
                  size="small"
                  label="Espera externa"
                  sx={{
                    color: '#fde68a',
                    bgcolor: alpha(
                      '#f59e0b',
                      0.15,
                    ),
                  }}
                />
              ) : null}
              {template.work_area_id ? (
                <Chip
                  size="small"
                  icon={
                    <WorkspacesRounded />
                  }
                  label={
                    workAreaNameById.get(
                      template.work_area_id,
                    )
                    ?? 'Área no disponible'
                  }
                  sx={{
                    color: '#bae6fd',
                    bgcolor: alpha(
                      '#0284c7',
                      0.16,
                    ),
                  }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Sin área"
                  sx={{
                    color: '#cbd5e1',
                    bgcolor: alpha(
                      '#64748b',
                      0.12,
                    ),
                  }}
                />
              )}

              {template.work_report_definition_id ? (
                <Chip
                  size="small"
                  label={`Parte · ${
                    workReportDefinitionNameById.get(
                      template.work_report_definition_id,
                    )
                    ?? 'No disponible'
                  }`}
                  sx={{
                    color: '#ddd6fe',
                    bgcolor: alpha(
                      '#8b5cf6',
                      0.16,
                    ),
                  }}
                />
              ) : null}

              <Chip
                size="small"
                label={
                  getPriorityLabel(
                    template.priority,
                  )
                }
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha(
                    '#64748b',
                    0.12,
                  ),
                }}
              />

              <Chip
                size="small"
                label={
                  template.due_offset_days
                  === null
                    ? 'Sin plazo'
                    : `${
                        template
                          .due_offset_days
                      } días`
                }
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha(
                    '#64748b',
                    0.12,
                  ),
                }}
              />

              <Chip
                size="small"
                label={
                  template.is_blocking
                    ? 'Obligatoria'
                    : 'Opcional'
                }
                sx={{
                  color:
                    template.is_blocking
                      ? '#fde68a'
                      : '#cbd5e1',
                  bgcolor:
                    template.is_blocking
                      ? alpha(
                          '#f59e0b',
                          0.15,
                        )
                      : alpha(
                          '#64748b',
                          0.12,
                        ),
                }}
              />

              {!template.is_active ? (
                <Chip
                  size="small"
                  label="Inactiva"
                  sx={{
                    color: '#cbd5e1',
                    bgcolor: alpha(
                      '#64748b',
                      0.12,
                    ),
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <IconButton
          size="small"
          aria-label="Editar tarea configurada"
          onClick={() =>
            handleEdit(
              template,
            )
          }
          sx={{
            color: '#94a3b8',
            alignSelf: {
              xs: 'flex-end',
              sm: 'flex-start',
            },
          }}
        >
          <EditRounded
            fontSize="small"
          />
        </IconButton>
      </Stack>
    </Paper>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${alpha(
          '#a78bfa',
          0.22,
        )}`,
        bgcolor:
          'rgba(15,23,42,0.66)',
        px: {
          xs: 1.6,
          md: 2,
        },
        py: 1.7,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            md: 'center',
          }}
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <PlaylistAddCheckRounded
              sx={{
                color: '#c4b5fd',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Trabajo al entrar
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  mt: 0.2,
                }}
              >
                Define las tareas que nacen al entrar
                en esta etapa y las acciones opcionales
                que podrán utilizarse cuando sean necesarias.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {templatesQuery.isError ? (
          <Alert
            severity="error"
            variant="outlined"
          >
            {getErrorMessage(
              templatesQuery.error,
            )}
          </Alert>
        ) : null}

        <Stack spacing={0.85}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            spacing={0.8}
          >
            <Box>
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontWeight: 900,
                  fontSize: '0.8rem',
                  letterSpacing: 0.65,
                  textTransform:
                    'uppercase',
                }}
              >
                Tareas iniciales
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.78rem',
                  mt: 0.2,
                }}
              >
                Se crean automáticamente cuando el
                expediente entra en la etapa.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRounded />}
              onClick={() =>
                handleCreate(
                  'initial',
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                whiteSpace: 'nowrap',
              }}
            >
              Añadir tarea inicial
            </Button>
          </Stack>

          {!initialTemplates.length ? (
            <Alert
              severity="info"
              variant="outlined"
            >
              Todavía no hay tareas iniciales configuradas.
            </Alert>
          ) : null}

          {initialTemplates.map(
            renderTemplate,
          )}
        </Stack>

        <Stack spacing={0.85}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            spacing={0.8}
          >
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="flex-start"
            >
              <TipsAndUpdatesRounded
                sx={{
                  color: '#fbbf24',
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#cbd5e1',
                    fontWeight: 900,
                    fontSize: '0.8rem',
                    letterSpacing: 0.65,
                    textTransform:
                      'uppercase',
                  }}
                >
                  Acciones opcionales sugeridas
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.78rem',
                    mt: 0.2,
                  }}
                >
                  No se crean automáticamente. Estarán disponibles
                  para utilizarlas únicamente cuando sean necesarias.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRounded />}
              onClick={() =>
                handleCreate(
                  'suggested',
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                whiteSpace: 'nowrap',
              }}
            >
              Añadir acción sugerida
            </Button>
          </Stack>

          {!suggestedTemplates.length ? (
            <Alert
              severity="info"
              variant="outlined"
            >
              Todavía no hay acciones opcionales sugeridas.
            </Alert>
          ) : null}

          {suggestedTemplates.map(
            renderTemplate,
          )}
        </Stack>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingTemplate
            ? 'Editar tarea configurada'
            : form.creation_mode
              === 'initial'
              ? 'Nueva tarea inicial'
              : 'Nueva acción sugerida'}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <TextField
              label="Título"
              value={
                form.title
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    title:
                      event.target.value,
                  }),
                )
              }
              fullWidth
              required
            />

            <TextField
              select
              label="Tipo de trabajo"
              value={
                form.task_type
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    task_type:
                      event.target
                        .value as
                          | 'internal_work'
                          | 'outside_system_wait',
                  }),
                )
              }
              helperText={
                form.task_type
                === 'outside_system_wait'
                  ? 'La acción depende de una respuesta externa.'
                  : 'La acción se ejecuta dentro de la organización.'
              }
              fullWidth
            >
              <MenuItem value="internal_work">
                Trabajo interno
              </MenuItem>

              <MenuItem value="outside_system_wait">
                Espera de respuesta externa
              </MenuItem>
            </TextField>

            <TextField
              label="Descripción"
              value={
                form.description
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    description:
                      event.target.value,
                  }),
                )
              }
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              select
              label="Área responsable"
              value={
                form.work_area_id
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    work_area_id:
                      event.target.value,
                  }),
                )
              }
              fullWidth
            >
              <MenuItem value="">
                Sin área responsable
              </MenuItem>

              {(
                workAreasQuery.data ?? []
              ).map(
                (area) => (
                  <MenuItem
                    key={area.id}
                    value={area.id}
                  >
                    {area.name}
                  </MenuItem>
                ),
              )}
            </TextField>

            <TextField
              select
              label="Parte asociado"
              value={
                form.work_report_definition_id
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    work_report_definition_id:
                      event.target.value,
                    task_type:
                      event.target.value
                        ? 'internal_work'
                        : current.task_type,
                  }),
                )
              }
              helperText="Si seleccionas un parte, se generará al asignar o iniciar esta tarea y será necesario completarlo para cerrar la tarea."
              fullWidth
            >
              <MenuItem value="">
                Sin parte asociado
              </MenuItem>

              {(
                workReportDefinitionsQuery.data ?? []
              )
                .filter(
                  (definition) =>
                    definition.is_active,
                )
                .map(
                  (
                    definition:
                      IndustrialWorkflowWorkReportDefinition,
                  ) => (
                    <MenuItem
                      key={definition.id}
                      value={definition.id}
                    >
                      {definition.name}
                    </MenuItem>
                  ),
                )}
            </TextField>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.2,
              }}
            >
              <TextField
                select
                label="Prioridad"
                value={
                  form.priority
                }
                onChange={(event) =>
                  setForm(
                    (current) => ({
                      ...current,
                      priority:
                        event.target
                          .value as IndustrialWorkflowCasePriority,
                    }),
                  )
                }
                fullWidth
              >
                <MenuItem value="low">
                  Baja
                </MenuItem>

                <MenuItem value="normal">
                  Normal
                </MenuItem>

                <MenuItem value="high">
                  Alta
                </MenuItem>

                <MenuItem value="urgent">
                  Urgente
                </MenuItem>
              </TextField>

              <TextField
                label="Plazo interno en días"
                value={
                  form.due_offset_days
                }
                onChange={(event) =>
                  setForm(
                    (current) => ({
                      ...current,
                      due_offset_days:
                        event.target.value,
                    }),
                  )
                }
                type="number"
                fullWidth
              />
            </Box>

            <Stack spacing={0.2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.is_blocking
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          is_blocking:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label={
                  form.creation_mode
                  === 'initial'
                    ? 'Debe completarse antes de finalizar la etapa'
                    : 'Si se utiliza, debe completarse antes de finalizar la etapa'
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.is_active
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          is_active:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label="Tarea disponible"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: 1 }}
          >
            {editingTemplate ? (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteRounded />}
                disabled={mutationIsPending}
                onClick={() =>
                  setTemplatePendingDelete(
                    editingTemplate,
                  )
                }
              >
                Eliminar
              </Button>
            ) : (
              <Box />
            )}

            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                onClick={
                  handleCloseDialog
                }
                disabled={
                  mutationIsPending
                }
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={
                  handleSave
                }
                disabled={
                  mutationIsPending
                  || !form.title.trim()
                }
              >
                Guardar
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(templatePendingDelete)}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setTemplatePendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {templatePendingDelete?.creation_mode
          === 'suggested'
            ? 'Eliminar acción sugerida'
            : 'Eliminar tarea inicial'}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={1.5}
            sx={{ pt: 1 }}
          >
            <Typography>
              Vas a eliminar{' '}
              <strong>
                {templatePendingDelete?.title}
              </strong>
              .
            </Typography>

            <Alert
              severity="warning"
              variant="outlined"
            >
              Solo se permitirá si todavía no se ha utilizado
              para generar tareas reales. Cuando ya exista
              actividad, desactívala para conservar la trazabilidad.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteMutation.isPending}
            onClick={() =>
              setTemplatePendingDelete(null)
            }
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={deleteMutation.isPending}
            onClick={handleConfirmDelete}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}